import React, { useContext, useState, useEffect } from "react";
import {
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from "react-router-dom";
import { FiCamera } from "react-icons/fi";
import { AuthContext } from "../../contexts/AuthContext";

// 🔁 adapte le chemin selon ton projet
import useSignalement from "../../hooks/useSignalement";
import useArticles from "../../hooks/useArticle";

const SignalementCreate = ({ setAlert }) => {
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const { createSignalement, getOneSignalement } = useSignalement();
  const { findOneArticle } = useArticles();

  const [form, setForm] = useState({
    typeSignalement: 2, // 1: Fraude, 2: Contenu inapproprié
    description: "",
    file: null,
    archive: 2,
    idArticle: "",
    idSignaleur: "",
    idUtilisateur: "",
    status: 1,
  });

  useEffect(() => {
    if (!id) return;
    setLoadingArticle(true); 
    findOneArticle(id)
      .then((res) => {
        if (res?.data?.data) {
          setForm((p) => ({ ...p, idUtilisateur: res.data.data.idUtilisateur }));
        }
        setLoadingArticle(false);
      })
      .catch((err) => {
        setLoadingArticle(false);
        setAlert?.({
          message:
            "Une erreur s'est produite lors de la récupération des données de l'annonce.",
          type: "danger",
        });
        console.error("Erreur lors de la récupération de l'article :", err);
      });
  }, [id]);

  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    if (id) return navigate(`/article/details/${id}`);
    return navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    if (!f) {
      setForm((p) => ({ ...p, file: null }));
      setPreviewImage(null);
      return;
    }

    // Optionnel: contrôle simple du type
    if (!f.type.startsWith("image/")) {
      setAlert?.({
        message: "Veuillez choisir une image (PNG/JPG/WebP).",
        type: "danger",
      });
      e.target.value = "";
      return;
    }

    setForm((p) => ({ ...p, file: f }));
    setPreviewImage(URL.createObjectURL(f));
  };

 const handleSubmit = async () => {
  if (!id) {
    return setAlert?.({
      message: "Article introuvable : id manquant dans l’URL.",
      type: "danger",
    });
  }
  if (!user?._id) {
    return setAlert?.({
      message: "Utilisateur non authentifié.",
      type: "danger",
    });
  }

  setLoading(true);
  try {
    const fd = new FormData();
    fd.append("typeSignalement", String(form.typeSignalement));
    fd.append("description", form.description || "");
    fd.append("idArticle", id);
    fd.append("idSignaleur", user._id);
    fd.append("idUtilisateur", form.idUtilisateur);
    fd.append("archive", form.archive);

    if (form.file) fd.append("file", form.file);

    await createSignalement(fd);

    setAlert?.({
      message: "Signalement envoyé avec succès.",
      type: "success",
    });
    navigate(-1);
  } catch (err) {
    console.error(err);

    const statusCode = err?.response?.status;
    const backendMsg = err?.response?.data?.message;

    // ✅ Cas "déjà signalé"
    if (statusCode === 409) {
      setAlert?.({
        type: "warning",
        message: backendMsg || "Vous avez déjà signalé cet article.",
      });
      return;
    }

    setAlert?.({
      message: backendMsg || "Une erreur s'est produite lors de l'envoi du signalement.",
      type: "danger",
    });
  } finally {
    setLoading(false);
  }
};


  return (
    <div className="container mt-5 mb-5">
      <i
        className="bi bi-x-circle login_icon_x"
        role="button"
        onClick={goBack}
      ></i>

      <div className="article_create_header">
        <h1 className="article_create_title">Signaler une annonce</h1>
      </div>

      {/* TYPE */}
      <div className="mb-3">
        <label className="form-label fw-bold">Type de signalement *</label>
        <select
          name="typeSignalement"
          className="form-select custom_css_field_update"
          value={form.typeSignalement}
          onChange={handleChange}
        >
          <option value={1}>Fraude</option>
          <option value={2}>Contenu inapproprié</option>
          <option value={2}>Contenu inapproprié</option>
        </select>
      </div>

      {/* DESCRIPTION */}
      <div className="mb-3">
        <label className="form-label fw-bold">Description</label>
        <textarea
          name="description"
          rows="4"
          className="form-control custom_css_field_update"
          value={form.description}
          onChange={handleChange}
          placeholder="Décrivez le problème…"
        />
      </div>

      {/* FILE */}
      <h6 className="fw-bold mb-2 d-flex align-items-center gap-2">
        <FiCamera /> Justification (facultatif)
      </h6>

      <div className="mb-3">
        <input
          type="file"
          accept="image/*"
          className="form-control"
          onChange={handleFileChange}
          style={{ borderRadius: "26px" }}
        />
        <small className="text-muted">
          Ajoutez une capture ou un document si vous en avez une (facultatif).
        </small>
      </div>

      {previewImage && (
        <div className="photo-flex-container mb-4">
          <div className="photo-card-flex">
            <img
              src={previewImage}
              className="photo-uploaded"
              alt="preuve"
              onClick={() => setPreviewImage(previewImage)}
            />
            <div className="photo-controls">
              <button
                className="btn-del"
                onClick={() => {
                  setForm((p) => ({ ...p, file: null }));
                  setPreviewImage(null);
                }}
              >
                <i className="bi bi-trash3" />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        className="article_create_btn_continuer mt-4 mb-5"
        onClick={handleSubmit}
        disabled={loading}
        style={{ width: "200px", float: "right" }}
      >
        {(loading || loadingArticle) ? "Envoi..." : "Envoyer"}
      </button>

      {/* Overlay preview (même UX que ton update) */}
      {previewImage && (
        <div
          className="image-preview-overlay"
          onClick={() => setPreviewImage(null)}
        >
          <img src={previewImage} alt="preview" />
        </div>
      )}
    </div>
  );
};

export default SignalementCreate;
